import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "image", "index", "total"]

  connect() {
    this.images = JSON.parse(this.element.dataset.galleryImagesValue || "[]")
    this.currentIndex = 0
    this.touchStartX = 0
    this.touchEndX = 0
  }

  disconnect() {
    this.toggleScrollLock(false, true)
  }

  open(event) {
    event.preventDefault()
    event.stopPropagation()
    const target = event.target
    const currentTarget = event.currentTarget

    const urlHost = target.closest?.("[data-url]") || currentTarget
    let clickedUrl = target.src || target.dataset?.url || urlHost.dataset?.url || currentTarget.dataset?.url
    
    // Normalize URL for comparison (remove protocol/host if present to match relative paths)
    const normalizeUrl = (url) => {
      if (!url) return ""
      try {
        const path = new URL(url, window.location.origin).pathname
        return path
      } catch (e) {
        return url
      }
    }

    const normalizedClicked = normalizeUrl(clickedUrl)
    
    // Find index by comparing normalized paths
    this.currentIndex = this.images.findIndex(img => normalizeUrl(img.url) === normalizedClicked)
    
    // If we clicked the "Show all photos" button or couldn't find match, default to first image
    if (this.currentIndex === -1) this.currentIndex = 0
    
    this.showImage()
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.style.display = "flex"
    this.modalTarget.style.zIndex = "10000"
    this.toggleScrollLock(true)

    // Add touch listeners to the modal
    this.modalTarget.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true })
    this.modalTarget.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true })
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this.modalTarget.style.display = "none"
    this.toggleScrollLock(false)
    
    // Remove touch listeners
    this.modalTarget.removeEventListener('touchstart', this.handleTouchStart)
    this.modalTarget.removeEventListener('touchend', this.handleTouchEnd)
  }

  handleTouchStart(event) {
    this.touchStartX = event.changedTouches[0].screenX
  }

  handleTouchEnd(event) {
    this.touchEndX = event.changedTouches[0].screenX
    this.handleSwipe()
  }

  handleSwipe() {
    const swipeThreshold = 50
    const deltaX = this.touchEndX - this.touchStartX

    if (deltaX < -swipeThreshold) {
      // Swipe Left -> Next
      this.next()
    } else if (deltaX > swipeThreshold) {
      // Swipe Right -> Previous
      this.prev()
    }
  }

  toggleScrollLock(lock, force = false) {
    const body = document.body
    const html = document.documentElement
    let openModals = parseInt(body.dataset.openModals || "0")

    if (lock) {
      openModals++
      body.dataset.openModals = openModals
      
      if (openModals === 1) {
        this.scrollPos = window.pageYOffset
        body.style.position = 'fixed'
        body.style.top = `-${this.scrollPos}px`
        body.style.width = '100%'
        body.classList.add('overflow-hidden')
        html.classList.add('overflow-hidden')
      }
    } else {
      openModals = force ? 0 : Math.max(0, openModals - 1)
      body.dataset.openModals = openModals
      
      if (openModals === 0) {
        const scrollY = body.style.top
        body.style.position = ''
        body.style.top = ''
        body.style.width = ''
        body.classList.remove('overflow-hidden')
        html.classList.remove('overflow-hidden')
        if (!force) {
          window.scrollTo(0, parseInt(scrollY || '0') * -1)
        }
      }
    }
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.images.length
    this.showImage()
  }

  prev() {
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    this.showImage()
  }

  showImage() {
    const currentImg = this.images[this.currentIndex]
    if (currentImg) {
      this.imageTarget.src = currentImg.url
      if (currentImg.alt) {
        this.imageTarget.alt = currentImg.alt
      }
      if (this.hasIndexTarget) this.indexTarget.textContent = this.currentIndex + 1
      if (this.hasTotalTarget) this.totalTarget.textContent = this.images.length
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.close()
    if (event.key === "ArrowRight") this.next()
    if (event.key === "ArrowLeft") this.prev()
  }
}
